import axios from 'axios'
import { useI18n } from 'vue-i18n'
import { useAxios } from '@/composables/useAxios'
import { getBackendErrorCode, getBackendErrorField } from '@/composables/useAxiosError'
import { STATUS_CODES } from '@/constants'
import { TermsConfig, TermsPendingList, TermsStatus } from '@/types'
import { isPlaceholder, termsSummaryKey } from '@/legal/termsBundle'

/** The account's standing against the terms; null for an answer of an unexpected shape. */
export const fetchTermsStatus = async (): Promise<TermsStatus | null> => {
  const res = await useAxios<null, TermsStatus>('GET', '/account/terms', true)
  const parsed = TermsStatus.safeParse(res.data)
  return parsed.success ? parsed.data : null
}

/** Published versions and their dates; public. */
export const fetchTermsConfig = async (): Promise<TermsConfig | null> => {
  const res = await useAxios<null, TermsConfig>('GET', '/config/terms', false)
  const parsed = TermsConfig.safeParse(res.data)
  return parsed.success ? parsed.data : null
}

export const fetchPendingTerms = async (): Promise<TermsPendingList | null> => {
  const res = await useAxios<null, TermsPendingList>('GET', '/admin/terms/pending', true)
  const parsed = TermsPendingList.safeParse(res.data)
  return parsed.success ? parsed.data : null
}

export type TermsAcceptResult =
  | { kind: 'accepted'; status: TermsStatus | null }
  /** A newer version was published since the page loaded; `currentVersion` is the one to show. */
  | { kind: 'stale'; currentVersion: string | null }
  /** 403: account_holder_required, email_not_verified or account_inactive. */
  | { kind: 'refused'; code: string | undefined }

/** Accepts `version`, the version whose text the page showed, for every contract of the account. */
export const acceptTerms = async (version: string): Promise<TermsAcceptResult> => {
  try {
    const res = await useAxios<{ version: string }, TermsStatus>('POST', '/account/terms/accept', true, { version })
    const parsed = TermsStatus.safeParse(res.data)
    return { kind: 'accepted', status: parsed.success ? parsed.data : null }
  } catch (err) {
    const status = axios.isAxiosError(err) ? err.response?.status : undefined
    const code = getBackendErrorCode(err)
    if (status === STATUS_CODES.CONFLICT && code === 'terms_version_stale') {
      return { kind: 'stale', currentVersion: getBackendErrorField<string>(err, 'current_version') ?? null }
    }
    if (status === STATUS_CODES.FORBIDDEN) return { kind: 'refused', code }
    throw err
  }
}

/**
 * The change summaries written for `ids`, in order, each a list of points;
 * versions without one, or with a placeholder point, are left out.
 */
export const useTermsSummaries = () => {
  const { t, tm } = useI18n()
  return (ids: string[]): { id: string; points: string[] }[] =>
    ids.flatMap((id) => {
      const key = `termsChanges.${termsSummaryKey(id)}.points`
      const raw: unknown = tm(key)
      if (!Array.isArray(raw) || raw.length === 0) return []
      const points = raw.map((_, i) => t(`${key}[${i}]`))
      return points.some(isPlaceholder) ? [] : [{ id, points }]
    })
}
